import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { posApi, saasApi } from '@/services/api';
import { Tags, Plus, Edit2, Loader2, AlertCircle, X, Upload } from 'lucide-react';
import CategoryIcon from '@/components/pos/CategoryIcon';
import IconPicker from '@/components/pos/IconPicker';
import ColorPicker from '@/components/pos/ColorPicker';
import { getDefaultCategoryColor } from '@/constants/posCategoryIcons';
import { resizeImage } from '@/utils/imageUpload';
import { resolveFileUrl } from '@/utils/fileUrl';
import type { POSCategory } from '@/types';

export default function CategoriesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<POSCategory | null>(null);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    category_name: '',
    category_type: 'Product',
    display_order: 0,
    pos_category_icon: '',
    pos_category_image: '',
    pos_category_color: '',
  });

  const [errorMsg, setErrorMsg] = useState('');

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categoriesList'],
    queryFn: async () => {
      const res = await posApi.getCategories();
      return res.data.message as POSCategory[];
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: unknown) => posApi.createCategory(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categoriesList'] });
      qc.invalidateQueries({ queryKey: ['posCategories'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      setErrorMsg(err.response?.data?.message || err.message || t('categories.failedCreate'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: unknown) => posApi.updateCategory(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categoriesList'] });
      qc.invalidateQueries({ queryKey: ['posCategories'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      setErrorMsg(err.response?.data?.message || err.message || t('categories.failedUpdate'));
    },
  });

  const resetForm = () => {
    setForm({
      category_name: '',
      category_type: 'Product',
      display_order: 0,
      pos_category_icon: '',
      pos_category_image: '',
      pos_category_color: '',
    });
    setEditing(null);
    setErrorMsg('');
  };

  const handleEdit = (cat: POSCategory) => {
    setEditing(cat);
    setForm({
      category_name: cat.category_name,
      category_type: cat.category_type || 'Product',
      display_order: cat.display_order || 0,
      pos_category_icon: cat.pos_category_icon || '',
      pos_category_image: cat.pos_category_image || '',
      pos_category_color: cat.pos_category_color || '',
    });
    setErrorMsg('');
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.category_name.trim()) {
      setErrorMsg(t('categories.nameRequired'));
      return;
    }

    const payload = {
      ...form,
      pos_category_color: form.pos_category_color || getDefaultCategoryColor(form.category_name),
    };

    if (editing) {
      updateMutation.mutate({ ...payload, name: editing.name });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const resized = await resizeImage(file, 400, 400);
      const uploadFile = new File([resized], file.name, { type: 'image/jpeg' });
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('is_private', '0');
      const res = await saasApi.uploadFile(formData);
      setForm((prev) => ({ ...prev, pos_category_image: res.data.message.file_url }));
    } catch (err) {
      setErrorMsg((err as Error)?.message || t('categories.imageUploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => setForm((prev) => ({ ...prev, pos_category_image: '' }));

  const effectiveColor = form.pos_category_color || getDefaultCategoryColor(form.category_name || 'Category');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('categories.title')}</h3>
          <p className="text-sm text-gray-500">{t('categories.subtitle')}</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t('categories.add')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50 text-gray-900 font-medium border-b border-gray-200">
              <tr>
                <th className="px-6 py-4">{t('categories.name')}</th>
                <th className="px-6 py-4">{t('categories.type')}</th>
                <th className="px-6 py-4">{t('categories.displayOrder')}</th>
                <th className="px-6 py-4">{t('categories.icon')}</th>
                <th className="px-6 py-4 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categories.map((cat) => {
                const color = cat.pos_category_color || getDefaultCategoryColor(cat.category_name);
                return (
                  <tr key={cat.name} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <CategoryIcon
                          icon={cat.pos_category_icon}
                          image={cat.pos_category_image}
                          color={color}
                          size="sm"
                        />
                        <span className="font-medium text-gray-900">{cat.category_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${cat.category_type === 'Service' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {cat.category_type || 'Product'}
                      </span>
                    </td>
                    <td className="px-6 py-4">{cat.display_order}</td>
                    <td className="px-6 py-4">
                      {cat.pos_category_icon || cat.pos_category_image ? (
                        <span className="text-xs text-gray-500">{cat.pos_category_icon || t('categories.image')}</span>
                      ) : (
                        <span className="text-xs text-gray-400">{t('common.none')}</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end">
                        <button
                          onClick={() => handleEdit(cat)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title={t('categories.edit')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {categories.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    {t('categories.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center gap-3 shrink-0">
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <Tags className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {editing ? t('categories.edit') : t('categories.add')}
                </h3>
                <p className="text-sm text-gray-500">
                  {editing ? t('categories.editSubtitle') : t('categories.addSubtitle')}
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
              <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6 overflow-y-auto flex-1 min-h-0">
              {errorMsg && (
                <div className="lg:col-span-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <p>{errorMsg}</p>
                </div>
              )}

              <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CategoryIcon
                  icon={form.pos_category_icon}
                  image={form.pos_category_image}
                  color={effectiveColor}
                  size="lg"
                />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.name')} *</label>
                  <input
                    type="text"
                    value={form.category_name}
                    onChange={(e) => setForm({ ...form, category_name: e.target.value })}
                    className="input-field w-full"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.type')} *</label>
                  <select
                    value={form.category_type}
                    onChange={(e) => setForm({ ...form, category_type: e.target.value })}
                    className="input-field w-full"
                  >
                    <option value="Product">{t('categories.product')}</option>
                    <option value="Service">{t('categories.service')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.displayOrder')}</label>
                  <input
                    type="number"
                    value={form.display_order}
                    onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })}
                    className="input-field w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.image')}</label>
                <div className="flex items-center gap-3">
                  {form.pos_category_image ? (
                    <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                      <img src={resolveFileUrl(form.pos_category_image)} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={clearImage}
                        className="absolute top-0.5 right-0.5 p-0.5 bg-black/50 text-white rounded-full hover:bg-black/70"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors">
                      <Upload className="w-4 h-4" />
                      {uploading ? t('common.uploading') : t('categories.uploadImage')}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploading}
                        className="hidden"
                      />
                    </label>
                  )}
                  <p className="text-xs text-gray-500">{t('categories.imageHint')}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.color')}</label>
                <ColorPicker
                  value={form.pos_category_color}
                  onChange={(color) => setForm((prev) => ({ ...prev, pos_category_color: color }))}
                />
                <p className="text-xs text-gray-500 mt-1">{t('categories.colorHint')}</p>
              </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('categories.icon')}</label>
                <IconPicker
                  value={form.pos_category_icon}
                  onChange={(name) => setForm((prev) => ({ ...prev, pos_category_icon: name }))}
                />
              </div>
              </div>

              <div className="px-5 py-4 border-t border-gray-100 flex gap-3 shrink-0 bg-white">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2.5 text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending || uploading}
                  className="flex-1 btn-primary flex justify-center items-center gap-2 py-2.5"
                >
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  {editing ? t('common.update') : t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
